import { ReactNode, useState } from 'react';
import { MenuButton as HeadlessMenuButton, Menu, MenuItem, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface DropdownOptionBase<T> {
  label: string;
  value: T;
  description?: string;
}

interface Props<T> {
  label: string;
  'data-testid'?: string;
  icon?: ReactNode;
  options: DropdownOptionBase<T>[];
  onClick?: (option: DropdownOptionBase<T>) => void;
  dropdownBefore?: ReactNode;
  dropdownAfter?: ReactNode;
}

function MenuButton<T>({
  label,
  'data-testid': dataTestId,
  icon,
  options,
  onClick,
  dropdownBefore,
  dropdownAfter,
}: Props<T>) {
  const [, setSelectedOption] = useState<DropdownOptionBase<T> | null>(null);

  return (
    <Menu as="div" className="relative inline-block text-left">
      <HeadlessMenuButton
        data-testid={dataTestId}
        className="bg-white text-gray-900 font-medium text-center border border-gray-300 px-4 py-2 h-[40px] rounded-lg text-sm hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-gray-200 flex items-center justify-center w-full"
      >
        {icon && <span className="mr-0.5">{icon}</span>}
        {label}
        <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
      </HeadlessMenuButton>
      <MenuItems className="z-10 origin-top-right absolute right-0 mt-2 w-full md:w-44 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
        <div
          className="py-1"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          {dropdownBefore && (
            <div className="px-4 py-2 border-b border-gray-200">{dropdownBefore}</div>
          )}

          {options.map((option) => (
            <MenuItem key={String(option.value)}>
              {({ focus }) => (
                <button
                  data-testid={
                    dataTestId
                      ? `${dataTestId}-option-${String(option.value).toLowerCase()}`
                      : undefined
                  }
                  onClick={() => {
                    setSelectedOption(option);
                    if (onClick) onClick(option);
                  }}
                  className={`${focus ? 'bg-gray-100' : ''} block px-4 py-2 text-sm text-gray-800 w-full text-left`}
                >
                  {option.label}
                  {option.description && (
                    <p className="mt-1 text-gray-600 text-xs">{option.description}</p>
                  )}
                </button>
              )}
            </MenuItem>
          ))}

          {dropdownAfter && (
            <>
              <div className="border-t border-gray-200 my-1"></div>
              <div className="px-4 py-2">{dropdownAfter}</div>
            </>
          )}
        </div>
      </MenuItems>
    </Menu>
  );
}

export default MenuButton;
